/** Why Beyond Accounts ended a session, when it said so (decision D26) */
export type Reason = 'expired' | 'revoked' | 'suspended';

/**
 * Why the session an arrival found had ended and which organizations the person stopped belonging to, so a
 * screen can say what happened and whether signing in again helps (decision D26). A session that ends while the
 * person works is answered by the page's `Session` of `@beyond-js/ui` instead.
 *
 * - `suspended` at arrival: the sign-in screen says signing in again does not help, and does not leave by itself.
 * - a lost membership: the person is still signed in, but no longer belongs to an organization they were
 *   using; signing in again does not help either, an owner or administrator of that organization can add them.
 */
export class Ending {
	static REASONS: readonly Reason[] = ['expired', 'revoked', 'suspended'];

	#reason: Reason | undefined;
	/** The reason the authority gave for the last session that ended, undefined when it gave none */
	get reason() {
		return this.#reason;
	}

	#departed = new Map<string, string>();

	/**
	 * Records why the session ended; a value outside the vocabulary is no reason
	 */
	record(reason: string | undefined) {
		this.#reason = Ending.REASONS.includes(reason as Reason) ? (reason as Reason) : undefined;
	}

	/** A new session began: nothing has ended */
	clear() {
		this.#reason = undefined;
	}

	/**
	 * Compares the organizations of the previous session document with the new one of the same person, and keeps
	 * the ones that disappeared with their names
	 */
	compare(previous: Array<{ organization: string; name?: string }>, current: Array<{ organization: string }>) {
		const kept = new Set(current.map(membership => membership.organization));
		for (const membership of previous) if (!kept.has(membership.organization)) this.#departed.set(membership.organization, membership.name ?? membership.organization);
		for (const organization of kept) this.#departed.delete(organization);
	}

	/**
	 * @returns The name of an organization the person stopped belonging to while signed in here, if they did
	 */
	departed(organization: string): string | undefined {
		return this.#departed.get(organization);
	}

	/** Another person signed in: what the previous one lost is not theirs */
	forget() {
		this.#departed.clear();
		this.#reason = undefined;
	}
}
